
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { useEffect } from "react";
import { toast } from "sonner";

interface DealAnalyticsProps {
  deals?: any[];
  className?: string;
}

// Define the shape of deal analytics - updated to match the actual data structure
interface DealAnalytic {
  probability_score: number;
  predicted_close_date: string;
}

// Updated type to match the actual structure from the database
interface Deal {
  id: string;
  name: string;
  stage: string;
  value: number;
  deal_analytics?: DealAnalytic[] | null; // Now correctly defined as an array or null
  [key: string]: any;
}

export const DealAnalytics = ({ className }: DealAnalyticsProps) => {
  const { data: deals, isLoading: isLoadingDeals, error: dealsError } = useQuery({
    queryKey: ["deal-data"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select(`
          *,
          deal_analytics (
            probability_score,
            predicted_close_date
          )
        `)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      // If no deals exist, create sample data
      if (!data || data.length === 0) {
        console.warn("No deals found, creating sample deals");
        await createSampleDeals();
        
        const { data: newData, error: newError } = await supabase
          .from("deals")
          .select(`
            *,
            deal_analytics (
              probability_score,
              predicted_close_date
            )
          `)
          .order("created_at", { ascending: false });
          
        if (newError) throw newError;
        return newData || [];
      }
      
      return data;
    },
  });

  // Create sample deals data
  const createSampleDeals = async () => {
    try {
      const dealStages = ['qualification', 'discovery', 'proposal', 'negotiation', 'closing', 'won', 'lost'];
      const sampleDeals = [];
      
      // Generate sample companies first
      const companyIds = [];
      for (let i = 0; i < 5; i++) {
        // Define a default workspace ID for sample data
        const defaultWorkspaceId = "00000000-0000-0000-0000-000000000000";
        
        const { data: company, error } = await supabase
          .from("companies")
          .insert({
            name: `Sample Company ${i + 1}`,
            industry: ["Technology", "Healthcare", "Finance", "Retail", "Manufacturing"][i],
            description: `This is sample company ${i + 1}`,
            website_url: `https://example${i + 1}.com`,
            engagement_score: Math.floor(Math.random() * 100),
            workspace_id: defaultWorkspaceId // Add the required workspace_id field
          })
          .select("id")
          .single();
          
        if (error) {
          console.error("Error creating sample company:", error);
          continue;
        }
        
        companyIds.push(company.id);
      }
      
      if (companyIds.length === 0) {
        // Fetch existing companies if we couldn't create new ones
        const { data: existingCompanies } = await supabase
          .from("companies")
          .select("id")
          .limit(5);
          
        if (existingCompanies && existingCompanies.length > 0) {
          companyIds.push(...existingCompanies.map(c => c.id));
        } else {
          throw new Error("No companies available for sample deals");
        }
      }
      
      // Generate sample deals
      for (let i = 0; i < 20; i++) {
        const stage = dealStages[Math.floor(Math.random() * dealStages.length)];
        const companyId = companyIds[Math.floor(Math.random() * companyIds.length)];
        const dealValue = Math.floor(Math.random() * 50000) + 10000;
        
        const expectedCloseDate = new Date();
        expectedCloseDate.setDate(expectedCloseDate.getDate() + Math.floor(Math.random() * 60) + 30);
        
        sampleDeals.push({
          name: `Sample Deal ${i + 1}`,
          stage,
          value: dealValue, // Store deal value for use in analytics
          company_id: companyId,
          workspace_id: "00000000-0000-0000-0000-000000000000", // Default workspace ID
          expected_close_date: expectedCloseDate.toISOString().split('T')[0],
          priority: ["low", "medium", "high"][Math.floor(Math.random() * 3)],
          notes: `Sample deal notes for deal ${i + 1}`
        });
      }
      
      // Insert deals
      for (const deal of sampleDeals) {
        const { data: newDeal, error } = await supabase
          .from("deals")
          .insert(deal)
          .select("id")
          .single();
          
        if (error) {
          console.error("Error creating sample deal:", error);
          continue;
        }
        
        // Create deal analytics
        await supabase
          .from("deal_analytics")
          .insert({
            deal_id: newDeal.id,
            probability_score: Math.floor(Math.random() * 100),
            predicted_close_date: deal.expected_close_date,
            competitor_analysis: {
              competitive_advantages: [
                "Superior technology",
                "Better pricing",
                "Faster delivery"
              ]
            },
            suggested_actions: [
              { priority: "high", action: "Send updated proposal" },
              { priority: "medium", action: "Schedule follow-up meeting" }
            ],
            price_recommendations: {
              minimum_price: deal.value * 0.8,
              recommended_price: deal.value,
              optimal_price: deal.value * 1.2,
              justification: "Based on market analysis and client budget"
            }
          });
      }
      
    } catch (error) {
      console.error("Error creating sample deals:", error);
      toast.error("Failed to create sample deals");
    }
  };

  // Handle data loading errors
  useEffect(() => {
    if (dealsError) {
      console.error("Error loading deals data:", dealsError);
      toast.error("Failed to load deals data");
    }
  }, [dealsError]);

  if (isLoadingDeals) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary"></div>
      </div>
    );
  }

  // Prepare data for pipeline value by stage
  const pipelineData = deals?.reduce((acc: any[], deal) => {
    const existingStage = acc.find((item) => item.stage === deal.stage);
    if (existingStage) {
      existingStage.value += deal.value || 0;
    } else {
      acc.push({
        stage: deal.stage,
        value: deal.value || 0,
      });
    }
    return acc;
  }, []) || [];

  // Prepare data for win probability
  const probabilityData = (deals || [])
    .filter((deal: any) => {
      // Make sure deal_analytics exists and is an array with at least one item
      return deal.deal_analytics && Array.isArray(deal.deal_analytics) && deal.deal_analytics.length > 0;
    })
    .map((deal: any) => ({
      name: deal.name.length > 15 ? deal.name.substring(0, 15) + '...' : deal.name,
      probability: deal.deal_analytics[0]?.probability_score || 0
    }));

  // Prepare data for deal stages distribution
  const stageDistribution = deals?.reduce((acc: Record<string, number>, deal) => {
    const stage = deal.stage || "unknown";
    acc[stage] = (acc[stage] || 0) + 1;
    return acc;
  }, {});

  const stageDistributionData = Object.entries(stageDistribution || {}).map(([name, value]) => ({ name, value }));

  // Enhanced colors with better contrast for dark mode
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#ec4899'];

  return (
    <div className={className}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-card/90 border-border shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-foreground">Pipeline Value by Stage</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="stage" stroke="rgba(255,255,255,0.7)" />
                <YAxis stroke="rgba(255,255,255,0.7)" />
                <Tooltip 
                  formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Value']}
                  contentStyle={{ backgroundColor: '#1f1f1f', border: '1px solid rgba(255,255,255,0.1)' }}
                />
                <Bar dataKey="value" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 bg-card/90 border-border shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-foreground">Deal Win Probability</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={probabilityData.slice(0, 10)}> {/* Limiting to top 10 for readability */}
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.7)" />
                <YAxis stroke="rgba(255,255,255,0.7)" />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Win Probability']}
                  contentStyle={{ backgroundColor: '#1f1f1f', border: '1px solid rgba(255,255,255,0.1)' }}
                />
                <Line
                  type="monotone"
                  dataKey="probability"
                  stroke="#10b981"
                  strokeWidth={2}
                  activeDot={{ r: 8, fill: "#10b981" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 bg-card/90 border-border shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-foreground">Deal Stage Distribution</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stageDistributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {stageDistributionData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value} deals`, 'Count']}
                  contentStyle={{ backgroundColor: '#1f1f1f', border: '1px solid rgba(255,255,255,0.1)' }}
                />
                <Legend formatter={(value) => <span className="text-foreground">{value}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 bg-card/90 border-border shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-foreground">Deal Value Distribution</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { range: '<$10k', count: deals?.filter(d => d.value < 10000).length || 0 },
                  { range: '$10k-$25k', count: deals?.filter(d => d.value >= 10000 && d.value < 25000).length || 0 },
                  { range: '$25k-$50k', count: deals?.filter(d => d.value >= 25000 && d.value < 50000).length || 0 },
                  { range: '$50k-$100k', count: deals?.filter(d => d.value >= 50000 && d.value < 100000).length || 0 },
                  { range: '>$100k', count: deals?.filter(d => d.value >= 100000).length || 0 },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="range" stroke="rgba(255,255,255,0.7)" />
                <YAxis stroke="rgba(255,255,255,0.7)" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f1f1f', border: '1px solid rgba(255,255,255,0.1)' }}
                />
                <Bar dataKey="count" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
};
